import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Grid, Paper, Typography, Button, TextField, MenuItem, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import CloseIcon from '@mui/icons-material/Close';
import BusinessCardContainer from './BusinessCardContainer';
import AppSearchBar from '../../../Components/AppSearchBar';
import routes from '../../../Utils/routes';

interface LocationState {
    businesses: any[];
    title: string;
    searchInput?: string;
    selectedFilterType?: string;
    selectedLocation?: string;
}

const AllBusinesses: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { businesses, title, searchInput: initialSearch, selectedFilterType: initialType, selectedLocation: initialLocation } = location.state as LocationState;

    const [searchInput, setSearchInput] = useState(initialSearch || '');
    const [selectedFilterType, setSelectedFilterType] = useState(initialType || '');
    const [selectedLocation, setSelectedLocation] = useState(initialLocation || '');
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [filteredBusinesses, setFilteredBusinesses] = useState(businesses);

    useEffect(() => {
        const filtered = businesses.filter(business => {
            const matchesSearch = searchInput.trim() === "" ||
                business.Name.toLowerCase().includes(searchInput.trim().toLowerCase());

            const matchesType = selectedFilterType === "" ||
                business.Business_Type__c === selectedFilterType;

            const matchesLocation = selectedLocation === "" ||
                (business.Business_Address__c &&
                    business.Business_Address__c.toLowerCase().includes(selectedLocation.toLowerCase()));

            return matchesSearch && matchesType && matchesLocation;
        });

        setFilteredBusinesses(filtered);
    }, [businesses, searchInput, selectedFilterType, selectedLocation]);

    const handleBack = () => {
        navigate(routes.businessdirectory);
    };

    const handleBusinessClick = (businessId: string) => {
        const selectedBusiness = businesses.find(business => business.Id === businessId);
        if (selectedBusiness) {
            navigate(`/business directory`, {
                state: {
                    businessName: selectedBusiness.Name,
                    businessSource: 'all',
                    selectedBusinessData: selectedBusiness,
                    selectedBusinessId: businessId,
                    view: 'detail'
                }
            });
        }
    };

    const distinctTypes = Array.from(
        new Set(businesses.map(business => business.Business_Type__c).filter(Boolean))
    ) as string[];

    const distinctLocations = Array.from(
        new Set(businesses.map(business => business.Business_Address__c).filter(Boolean))
    ) as string[];

    const transformedBusinessCards = filteredBusinesses.map(business => {
        const defaultContact = {
            name: "Unknown Contact",
            image: "https://thumbs.dreamstime.com/b/creative-illustration-default-avatar-profile-placeholder-isolated-background-art-design-grey-photo-blank-template-mockup-144855718.jpg"
        };

        let contactInfo = defaultContact;
        if (business.Business_Directories__r && business.Business_Directories__r.records.length > 0) {
            const firstContact = business.Business_Directories__r.records[0].Contact__r;
            contactInfo = {
                name: firstContact.Name || defaultContact.name,
                image: firstContact.Profile_Picture_URL__c || defaultContact.image
            };
        }

        return {
            id: business.Id,
            businessImage: business.Cover_Image_Url__c || "https://ample.co.in/wp-content/uploads/2023/12/img-main.jpg",
            logo: business.Logo_URL__c || "https://upload.wikimedia.org/wikipedia/commons/a/ac/No_image_available.svg",
            category: business.Business_Type__c || "No Category",
            name: business.Name,
            location: business.Business_Address__c || "No Location",
            contactName: contactInfo.name,
            contactImage: contactInfo.image
        };
    });

    return (
        <Box sx={{ 
            p: { xs: 1, sm: 2 },
            height: { sm: 'auto' }
        }}>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Box display="flex" alignItems="center" mb={3}>
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={handleBack}
                            sx={{
                                color: "#373A45",
                                fontWeight: 500,
                                mr: 2
                            }}
                        >
                            {title}
                        </Button>
                    </Box>

                    <Grid container spacing={2} alignItems="center" mb={3}>
                        <Grid item xs={12} md={8}>
                            <AppSearchBar
                                localSearchInput={searchInput}
                                handleChange={(e) => setSearchInput(e.target.value)}
                                handleMicHold={() => { }}
                                handleMicRelease={() => { }}
                                openSnackbar={false}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Button
                                fullWidth
                                variant="outlined"
                                startIcon={<FilterAltOutlinedIcon />}
                                onClick={() => setIsFilterOpen(true)}
                                sx={{
                                    borderColor: '#B2B3B7',
                                    color: '#373A45',
                                    '&:hover': {
                                        borderColor: '#123A6F',
                                        backgroundColor: 'rgba(18, 58, 111, 0.04)'
                                    }
                                }}
                            >
                                Filter
                            </Button>
                        </Grid>
                    </Grid>

                    {isFilterOpen && (
                        <Paper
                            sx={{
                                position: 'fixed',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: { xs: '90%', sm: 400 },
                                maxHeight: { xs: '90vh', sm: 'auto' },
                                overflowY: { xs: 'auto', sm: 'visible' },
                                zIndex: 1300,
                                p: { xs: 2, sm: 3 },
                                borderRadius: { xs: '12px', sm: '8px' },
                                boxShadow: { xs: '0px 4px 20px rgba(0, 0, 0, 0.08)', sm: '0px 2px 8px rgba(0, 0, 0, 0.1)' }
                            }}
                        >
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '18px' }}>Filters</Typography>
                                <IconButton onClick={() => setIsFilterOpen(false)}>
                                    <CloseIcon />
                                </IconButton>
                            </Box>

                            <Box mb={3}>
                                <Typography 
                                    sx={{ 
                                        fontSize: '14px', 
                                        fontWeight: 500, 
                                        color: '#373A45',
                                        mb: 1,
                                        textAlign: 'left'
                                    }}
                                >
                                    Business Type
                                </Typography>
                                <select
                                    value={selectedFilterType}
                                    onChange={(e) => setSelectedFilterType(e.target.value)}
                                    style={{
                                        width: '100%',
                                        height: '36px',
                                        border: '0.5px solid #B2B3B7',
                                        borderRadius: '8px',
                                        padding: '0 12px',
                                        backgroundColor: 'white',
                                        fontSize: '14px',
                                        color: '#373A45',
                                        outline: 'none',
                                        cursor: 'pointer',
                                        appearance: 'none',
                                        backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                                        backgroundRepeat: 'no-repeat',
                                        backgroundPosition: 'right 12px center',
                                        backgroundSize: '16px'
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.border = '1px solid #123A6F';
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.border = '0.5px solid #B2B3B7';
                                    }}
                                >
                                    <option value="">All Types</option>
                                    {distinctTypes.map((type) => (
                                        <option key={type} value={type}>
                                            {type}
                                        </option>
                                    ))}
                                </select>
                            </Box>

                            <Box mb={3}>
                                <Typography 
                                    sx={{ 
                                        fontSize: '14px', 
                                        fontWeight: 500, 
                                        color: '#373A45',
                                        mb: 1,
                                        textAlign: 'left'
                                    }}
                                >
                                    Location
                                </Typography>
                                <select
                                    value={selectedLocation}
                                    onChange={(e) => setSelectedLocation(e.target.value)}
                                    style={{
                                        width: '100%',
                                        height: '36px',
                                        border: '0.5px solid #B2B3B7',
                                        borderRadius: '8px',
                                        padding: '0 12px',
                                        backgroundColor: 'white',
                                        fontSize: '14px',
                                        color: '#373A45',
                                        outline: 'none',
                                        cursor: 'pointer',
                                        appearance: 'none',
                                        backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                                        backgroundRepeat: 'no-repeat',
                                        backgroundPosition: 'right 12px center',
                                        backgroundSize: '16px'
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.border = '1px solid #123A6F';
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.border = '0.5px solid #B2B3B7';
                                    }}
                                >
                                    <option value="">All Locations</option>
                                    {distinctLocations.map((location) => (
                                        <option key={location} value={location}>
                                            {location}
                                        </option>
                                    ))}
                                </select>
                            </Box>

                            <Box 
                                display="flex" 
                                justifyContent="space-between" 
                                mt={3}
                                sx={{
                                    position: 'sticky',
                                    bottom: 0,
                                    backgroundColor: 'white',
                                    pt: 2,
                                    borderTop: '1px solid #E4E4E7'
                                }}
                            >
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        setSelectedFilterType('');
                                        setSelectedLocation('');
                                    }}
                                    sx={{ 
                                        mr: 1,
                                        borderColor: '#D1D5DB',
                                        color: '#111827',
                                        '&:hover': {
                                            borderColor: '#123A6F',
                                            backgroundColor: 'rgba(18, 58, 111, 0.04)'
                                        }
                                    }}
                                >
                                    Clear
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={() => setIsFilterOpen(false)}
                                    sx={{
                                        background: 'linear-gradient(90deg, #00387A -30.21%, #006BB7 100%)',
                                        '&:hover': { 
                                            background: 'linear-gradient(90deg, #002A5C -30.21%, #005A9C 100%)'
                                        }
                                    }}
                                >
                                    Apply
                                </Button>
                            </Box>
                        </Paper>
                    )}

                    <Paper 
                        elevation={0} 
                        sx={{ 
                            p: { xs: 1, sm: 2 },
                            borderRadius: { xs: '9px', sm: '8px' },
                            backgroundColor: 'white',
                            height: { sm: 'auto' }
                        }}
                    >
                        <BusinessCardContainer
                            cardData={transformedBusinessCards}
                            onCardClick={handleBusinessClick}
                        />
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default AllBusinesses; 